import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Target, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { api, getCurriculum, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TINTS = { es: "from-amber-100 to-rose-100", fr: "from-sky-100 to-indigo-100", de: "from-yellow-100 to-orange-100", ja: "from-rose-100 to-pink-50", it: "from-emerald-100 to-lime-50" };

export default function Languages() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { refreshUser } = useAuth();
  const [cur, setCur] = useState(null);
  const [started, setStarted] = useState({});
  const [picked, setPicked] = useState(params.get("lang"));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getCurriculum().then(setCur);
    api.get("/dashboard").then(({ data }) => setStarted(Object.fromEntries(data.languages.map((l) => [l.language, l]))));
  }, []);

  const start = async (level) => {
    setBusy(true);
    try {
      await api.post("/progress/start", { language: picked, level });
      await refreshUser();
      navigate(`/learn/${picked}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (!cur) return null;
  const lang = cur.languages.find((l) => l.code === picked);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
      <p className="caption">Step 1</p>
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 mt-1">Which language calls to you?</h1>
      <p className="text-slate-600 mt-2">You can learn more than one. Progress is saved separately for each.</p>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-8">
        {cur.languages.map((l, i) => (
          <button
            key={l.code}
            data-testid={`language-select-${l.code}`}
            onClick={() => setPicked(l.code)}
            style={{ animationDelay: `${i * 60}ms` }}
            className={`animate-fade-up relative text-left rounded-3xl p-5 bg-gradient-to-br ${TINTS[l.code]} border-2 transition-[transform,border-color,box-shadow] hover:-translate-y-1 ${picked === l.code ? "border-[hsl(var(--primary))] shadow-lg" : "border-transparent"}`}
          >
            {started[l.code] && <span className="absolute top-3 right-3 text-[10px] font-bold uppercase bg-white/80 rounded-full px-2 py-0.5 text-slate-700">Started</span>}
            <p className="font-display text-2xl font-extrabold text-slate-900">{l.greeting}</p>
            <p className="mt-6 font-bold text-slate-900">{l.name}</p>
            <p className="text-sm text-slate-600">{l.native}</p>
            {picked === l.code && <Check className="absolute bottom-4 right-4 text-[hsl(var(--primary))]" />}
          </button>
        ))}
      </div>

      {lang && (
        <div key={lang.code} className="mt-12 animate-fade-up" data-testid="level-choice-panel">
          <p className="caption">Step 2</p>
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-slate-900 mt-1">Where should you start in {lang.name}?</h2>
          {started[lang.code] && (
            <button data-testid="continue-language-button" onClick={() => navigate(`/learn/${lang.code}`)} className="btn-primary mt-5">
              Continue {lang.name} ({started[lang.code].current_level}) <ArrowRight size={18} />
            </button>
          )}
          <button
            data-testid="placement-test-button"
            onClick={() => navigate(`/placement/${lang.code}`)}
            className="mt-6 w-full text-left rounded-3xl p-6 bg-[hsl(var(--accent-blue-light))] border-2 border-[hsl(var(--accent-blue))]/30 flex items-center gap-4 hover:-translate-y-0.5 transition-transform"
          >
            <span className="h-12 w-12 rounded-2xl bg-white grid place-items-center"><Target className="text-[hsl(var(--accent-blue))]" /></span>
            <span className="flex-1">
              <span className="block font-display font-bold text-lg text-slate-900">Not sure? Take a 9-question placement test</span>
              <span className="block text-sm text-slate-600">About 3 minutes. We'll suggest the right level.</span>
            </span>
            <ArrowRight className="text-[hsl(var(--accent-blue))]" />
          </button>
          <div className="grid md:grid-cols-3 gap-4 mt-4">
            {cur.levels.map((lv, i) => (
              <button
                key={lv.id}
                disabled={busy}
                data-testid={`level-select-${lv.id}`}
                onClick={() => start(lv.id)}
                className="text-left rounded-3xl p-6 bg-white border-2 border-slate-200 hover:border-[hsl(var(--primary))] hover:-translate-y-0.5 transition-[transform,border-color]"
              >
                <span className="caption">Level {i + 1}</span>
                <span className="block font-display text-xl font-extrabold text-slate-900 mt-1">{lv.name}</span>
                <span className="block text-sm text-slate-600 mt-1">{lv.desc}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
